/**
 * The "About this page" dialog behind a page title's ⓘ: what the reader can
 * do on this page, one expandable task each, and — where the control is on
 * screen right now — a "Show me" that closes the dialog and points at it.
 *
 * The search box looks across every page the reader can open. Word matches
 * render on the keystroke; related tasks found by meaning arrive a moment
 * later below them (see search/usePageInfoSearch). A result from another page
 * says so, and links there with the task already open.
 *
 * Tasks a reader's permissions rule out are left out entirely: describing an
 * action and then refusing it is worse than not mentioning it.
 */
import { useMemo, useRef, useState, type MutableRefObject, type RefObject } from "react";
import { Link } from "react-router-dom";

import { Accordion } from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { PageInfoId } from "@/lib/i18n/types/pageInfo";
import { cn } from "@/lib/utils";
import { highlightPageInfoTarget } from "./highlightPageInfoTarget";
import { PAGE_INFO, PAGE_INFO_GUIDE_ROUTE } from "./pageInfoRegistry";
import { PageInfoSearchBox, SearchResults, TaskItem } from "./PageInfoTaskList";
import { useVisibleChunks, type PageInfoScope } from "./visibleChunks";
import { usePageInfoSearch } from "./search/usePageInfoSearch";

export type { PageInfoScope };

export interface PageInfoDialogProps {
  page: PageInfoId;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The page's scope, for the tasks' permission gates and other pages' links. */
  scope: PageInfoScope;
  /** A task to show expanded when the dialog opens (arriving from another page's search). */
  initialItem?: string | null;
  /**
   * Where focus goes back to on close. Radix returns it to whatever had focus
   * before opening, which is nothing when the trigger was clicked in Safari
   * (it does not focus buttons on click).
   */
  returnFocusRef?: RefObject<HTMLElement>;
  /** Raise the backdrop when opened from a raised host (the fullscreen 3D viewer). */
  overlayClassName?: string;
  contentClassName?: string;
}

export function PageInfoDialog({
  page,
  open,
  onOpenChange,
  scope,
  initialItem,
  returnFocusRef,
  overlayClassName,
  contentClassName,
}: PageInfoDialogProps) {
  // Set by "Show me" just before closing; consumed once focus would return.
  const pendingTarget = useRef<string | null>(null);
  // "Go to page" navigates away; focus must not be pulled back to this page's ⓘ.
  const leaving = useRef(false);
  // Escape clears a search before it closes the dialog. Radix listens for
  // Escape in the capture phase, so the search box cannot intercept it itself.
  const clearSearch = useRef<() => boolean>(() => false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName={overlayClassName}
        className={cn(
          "flex h-[min(88vh,860px)] max-w-3xl flex-col gap-0 overflow-hidden p-0",
          contentClassName
        )}
        onEscapeKeyDown={(event) => {
          if (clearSearch.current()) event.preventDefault();
        }}
        onCloseAutoFocus={(event) => {
          const target = pendingTarget.current;
          pendingTarget.current = null;
          if (leaving.current) {
            leaving.current = false;
            event.preventDefault();
            return;
          }
          // The highlighted control takes focus instead of the ⓘ.
          if (target && highlightPageInfoTarget(target)) {
            event.preventDefault();
            return;
          }
          const trigger = returnFocusRef?.current;
          if (trigger?.isConnected) {
            event.preventDefault();
            trigger.focus();
          }
        }}
      >
        {/* Radix mounts the content only while open (and through its close
            animation), so the body's permission read never runs for a closed dialog. */}
        <PageInfoBody
          page={page}
          scope={scope}
          initialItem={initialItem ?? null}
          clearSearchRef={clearSearch}
          onShowMe={(target) => {
            pendingTarget.current = target;
            onOpenChange(false);
          }}
          onLeave={() => {
            leaving.current = true;
            onOpenChange(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

interface BodyProps {
  page: PageInfoId;
  scope: PageInfoScope;
  initialItem: string | null;
  /** Set to "clear the query if there is one; say whether there was". */
  clearSearchRef: MutableRefObject<() => boolean>;
  onShowMe: (target: string) => void;
  onLeave: () => void;
}

function PageInfoBody({ page, scope, initialItem, clearSearchRef, onShowMe, onLeave }: BodyProps) {
  const { t, language } = useLanguage();
  const [query, setQuery] = useState("");
  clearSearchRef.current = () => {
    if (!query) return false;
    setQuery("");
    return true;
  };
  const def = PAGE_INFO[page];
  const copy = t.pageInfo.pages[page];

  const chunks = useVisibleChunks(scope, page);
  const pageChunks = useMemo(() => chunks.filter((chunk) => chunk.page === page), [chunks, page]);

  const search = usePageInfoSearch(query, chunks, language);
  const searching = search.query.length > 0;

  return (
    <>
      <DialogHeader className="space-y-1.5 border-b px-6 pb-4 pt-6 text-left">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t.pageInfo.eyebrow}
        </p>
        <DialogTitle className="pr-6">{def.title(t)}</DialogTitle>
        <DialogDescription className="leading-relaxed">{copy.intro}</DialogDescription>
        <PageInfoSearchBox query={query} onQueryChange={setQuery} className="pt-3" />
      </DialogHeader>

      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-2">
        {searching ? (
          <SearchResults
            key={search.query}
            page={page}
            scope={scope}
            search={search}
            onShowMe={onShowMe}
            onLeave={onLeave}
          />
        ) : (
          <Accordion
            type="single"
            collapsible
            defaultValue={initialItem ? `${page}.${initialItem}` : undefined}
          >
            {pageChunks.map((chunk) => (
              <TaskItem
                key={chunk.key}
                chunk={chunk}
                page={page}
                scope={scope}
                onShowMe={onShowMe}
                onLeave={onLeave}
              />
            ))}
          </Accordion>
        )}
      </div>

      {page !== "help" && (
        <div className="border-t px-6 py-3 text-sm">
          <Link
            to={PAGE_INFO_GUIDE_ROUTE}
            onClick={onLeave}
            className="font-medium text-primary hover:underline"
          >
            {t.pageInfo.moreHelp}
          </Link>
        </div>
      )}
    </>
  );
}
